import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_COWORK_MCP_CONNECTOR,
  ROTATE_COWORK_MCP_CREDENTIALS,
} from '../../graphql/mutations';
import { GET_COWORK_MCP_CONNECTOR } from '../../graphql/queries';
import { useCopy } from '../../lib/copyToClipboard';
import type { CoworkMcpCredentials } from '../../types/graphql';
import { ConnDialog, CxCopyRow, CxInput, CxSteps, RadioCards } from './ConnDialog';

/**
 * Connections → Connect an agent / Rotate secret (Prototype v15). Connect:
 * what it is and a label, then its credentials — shown once — with the steps
 * for that kind of client, and a line that turns green when the agent first
 * calls in. Rotate: a warning, then the new secret the same way.
 *
 * The agents are the ones the signup Agent step offers (cms/agentGuides.ts),
 * plus Gemini Enterprise (an admin's Cloud console setup, never an
 * onboarding step), but every agent here gets its own key, so the steps paste the key rather
 * than signing in with email (that path binds to the first agent's key).
 *
 * Grok can add a connector from a chat message, so its credentials open with
 * a ready-made message to paste; the others say in one line why they can't.
 * ChatGPT (plugins) takes only the address and OAuth, then signs the person
 * in with their email — so it binds to the first agent's key, not this one.
 * The Client ID is the smk_ token (scope `mcp`), which is also the bearer
 * token, so Claude Code and VS Code send it as their "secret".
 */

/** Where a new agent starts; its rules can change later on its card. */
type AgentRole = 'ask' | 'drafts_only';

const ROLES: Array<{ value: AgentRole; title: string; summary: string }> = [
  {
    value: 'ask',
    title: 'Draft, and ask first',
    summary: 'Writes drafts and asks you before anything goes out.',
  },
  {
    value: 'drafts_only',
    title: 'Drafts only',
    summary: 'Writes drafts and nothing else.',
  },
];

type AgentType =
  | 'cowork'
  | 'code'
  | 'grok'
  | 'chatgpt'
  | 'gemini'
  | 'geminienterprise'
  | 'm365copilot'
  | 'githubcopilot'
  | 'other';

const TYPES: Array<{
  value: AgentType;
  title: string;
  summary: string;
  app: string;
  /** Why there's no message to paste. Unset: it sets itself up from one. */
  noMessage?: string;
}> = [
  {
    value: 'cowork',
    title: 'Claude',
    summary: 'Cowork, claude.ai and the Claude apps.',
    app: 'Claude',
    noMessage: 'Claude can’t add connectors from a chat message, so add it in Customize.',
  },
  {
    value: 'code',
    title: 'Claude Code',
    summary: 'Works from the terminal or your code editor.',
    app: 'Claude Code',
    noMessage: 'Claude Code adds connectors with a command, not a chat message.',
  },
  {
    value: 'grok',
    title: 'Grok',
    summary: 'Sets itself up when you paste one message into a chat.',
    app: 'Grok',
  },
  {
    value: 'chatgpt',
    title: 'ChatGPT',
    summary: 'chatgpt.com, as a plugin.',
    app: 'ChatGPT',
    noMessage: 'ChatGPT adds MCP servers from its Plugins page, not from a chat message.',
  },
  {
    value: 'gemini',
    title: 'Gemini',
    summary: 'gemini.google.com and the Gemini apps.',
    app: 'Gemini',
    noMessage: 'Gemini adds custom apps from its Connected Apps settings, not from a chat message.',
  },
  {
    value: 'geminienterprise',
    title: 'Gemini Enterprise',
    summary: 'Google Cloud, set up by an admin.',
    app: 'Gemini Enterprise',
    noMessage: 'An admin adds MCP servers to Gemini Enterprise, not a chat message.',
  },
  {
    value: 'm365copilot',
    title: 'Microsoft Copilot',
    summary: 'Microsoft 365 Copilot, through Copilot Studio.',
    app: 'Copilot Studio',
    noMessage: 'Copilot can’t add connectors from a chat message, so add it in Copilot Studio.',
  },
  {
    value: 'githubcopilot',
    title: 'GitHub Copilot',
    summary: 'Copilot in VS Code.',
    app: 'VS Code',
    noMessage: 'Copilot adds servers from a file in your project, not a chat message.',
  },
  {
    value: 'other',
    title: 'Other MCP client',
    summary: 'Any app that can add a remote MCP server.',
    app: 'your app',
    noMessage:
      'Most apps can’t add connectors from a chat message, so add it in the app’s settings.',
  },
];
const typeOf = (t: AgentType) => TYPES.find((x) => x.value === t) || TYPES[TYPES.length - 1];
const appOf = (t: AgentType) => typeOf(t).app;
/** These send the Client ID as a bearer token, so it's the only secret they see. */
const bearerOnly = (t: AgentType) => t === 'code' || t === 'githubcopilot';

/** The message an agent that sets itself up gets pasted into a new chat. */
function setupMessage({ mcpUrl, clientId, clientSecret }: CoworkMcpCredentials) {
  return `Can you add a custom connector for me? The details are: MCP URL: ${mcpUrl}, client ID: ${clientId}, client secret: ${clientSecret}. Name it tinycms. When it’s connected, list my 3 latest tinycms articles so I know it works.`;
}

function setupSteps(type: AgentType, creds: CoworkMcpCredentials, rotate: boolean) {
  const { mcpUrl, clientId } = creds;
  if (rotate) {
    return [
      { t: 'In Claude, open Customize → Connectors → tinycms and choose Edit.' },
      { t: 'Under Advanced settings, replace the old secret with this one and save.' },
    ];
  }
  switch (type) {
    case 'code':
      return [
        {
          t: 'Run this in the project you want it to work in:',
          code: `claude mcp add --transport http tinycms ${mcpUrl} --header "Authorization: Bearer ${clientId}"`,
        },
        { t: 'Start Claude Code and ask it to list your tinycms articles.' },
      ];
    case 'grok':
      return [
        { t: 'Go to grok.com/connectors, choose New Connector, then Custom.' },
        { t: 'Name it tinycms and paste the MCP address, client ID and client secret.' },
        { t: 'Ask it to list your tinycms articles to check it works.' },
      ];
    case 'chatgpt':
      return [
        {
          t: 'In ChatGPT on the web, open Plugins (chatgpt.com/plugins) → + → Add custom MCP server.',
        },
        { t: 'Name it tinycms and paste the MCP address.' },
        {
          t: 'Under Authentication choose OAuth. ChatGPT signs you in with your email and doesn’t take the client ID or secret.',
        },
        { t: 'Accept the warning and choose Create as a plugin.' },
        { t: 'Install it from your plugins, then type @ in a new chat to pick it.' },
      ];
    case 'gemini':
      return [
        {
          t: 'In Gemini on the web, open Settings → Connected Apps → Custom apps → Add a custom app.',
        },
        { t: 'Paste the MCP address.' },
        {
          t: 'Choose Show more next to Advanced features and paste the client ID and client secret.',
        },
        { t: 'Choose Next and sign in when asked, then type @ in a new chat to pick it.' },
      ];
    case 'geminienterprise':
      return [
        {
          t: 'Google blocks custom MCP servers by default. In the Google Cloud console, open IAM & Admin → Organization policies, find “Disable custom MCP server connector for Gemini Enterprise”, choose Manage policy → Override parent’s policy, and set it to Off.',
        },
        {
          t: 'In Gemini Enterprise → Settings → Authentication, check your app’s location uses Google Identity.',
        },
        {
          t: 'Open your app → Connected data stores → New data store, search for Custom MCP Server and choose Add MCP server.',
        },
        {
          t: 'Choose OAuth 2.0. Paste the MCP address, client ID, client secret and these, turn on Enable PKCE Support, then choose Verify Auth and sign in.',
          code: `Authorization URL  ${mcpUrl}/oauth/authorize\nToken URL          ${mcpUrl}/oauth/token\nScopes             mcp offline_access`,
        },
        { t: 'Choose Continue, name it after your site, and Create.' },
        {
          t: 'When it shows Active, choose Re-authenticate and sign in again. Then on the Actions tab choose Reload custom actions, select the actions and choose Enable actions (50 show per page).',
        },
        {
          t: 'In a Gemini Enterprise chat, open the connectors menu and choose Authorize next to it. Each person does this once.',
        },
      ];
    case 'm365copilot':
      return [
        { t: 'In Copilot Studio, open your agent → Tools → Add a tool → Model Context Protocol.' },
        { t: 'Name it tinycms and paste the MCP address.' },
        {
          t: 'For authentication choose OAuth 2.0, then Manual. Paste the client ID, client secret and these:',
          code: `Authorization URL  ${mcpUrl}/oauth/authorize\nToken URL          ${mcpUrl}/oauth/token\nRefresh URL        ${mcpUrl}/oauth/token\nScope              mcp`,
        },
        { t: 'Create the tool, add it to your agent and sign in when asked.' },
      ];
    case 'githubcopilot':
      return [
        {
          t: 'In your project, create .vscode/mcp.json with this:',
          code: JSON.stringify(
            {
              servers: {
                tinycms: {
                  type: 'http',
                  url: mcpUrl,
                  headers: { Authorization: `Bearer ${clientId}` },
                },
              },
            },
            null,
            2
          ),
        },
        { t: 'Select Start above the server name, then use Copilot Chat in Agent mode.' },
      ];
    case 'other':
      return [
        { t: 'Add a remote MCP server with the MCP address.' },
        {
          t: 'If it asks for OAuth client details, paste the client ID and client secret. If it asks for a bearer token, paste the client ID.',
        },
        { t: 'Ask it to list your tinycms articles to check it works.' },
      ];
    default:
      return [
        { t: 'In Claude, open Customize → Connectors, choose + Add, then Add custom connector.' },
        { t: 'Name it tinycms and paste the MCP address.' },
        {
          t: 'Open Advanced settings, paste the client ID and client secret, then finish adding it and connect.',
        },
      ];
  }
}

export function AgentDialog({
  accountId,
  mode,
  currentLabel,
  onClose,
  onChanged,
}: {
  accountId: string;
  mode: 'connect' | 'rotate';
  /** Rotate: the connected agent's name. */
  currentLabel?: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [type, setType] = useState<AgentType>('cowork');
  const [label, setLabel] = useState('');
  const [role, setRole] = useState<AgentRole>('ask');
  const [tried, setTried] = useState(false);
  const [issued, setIssued] = useState<{ creds: CoworkMcpCredentials; at: number } | null>(null);
  const [error, setError] = useState('');
  const { copy, state } = useCopy();
  const [create, { loading: creating }] = useMutation(CREATE_COWORK_MCP_CONNECTOR);
  const [rotate, { loading: rotating }] = useMutation(ROTATE_COWORK_MCP_CREDENTIALS);
  const name = mode === 'rotate' ? currentLabel || 'Your agent' : label.trim();

  // Once issued, look every few seconds for the agent's first call.
  const { data } = useQuery<{ coworkMcpConnector: { lastUsedAt?: string | null } | null }>(
    GET_COWORK_MCP_CONNECTOR,
    { variables: { accountId }, skip: !issued, pollInterval: issued ? 5000 : 0 }
  );
  const lastUsed = data?.coworkMcpConnector?.lastUsedAt;
  const connected = Boolean(issued && lastUsed && new Date(lastUsed).getTime() > issued.at - 5000);

  const steps = useMemo(
    () => (issued ? setupSteps(type, issued.creds, mode === 'rotate') : []),
    [issued, type, mode]
  );

  if (issued) {
    const rotated = mode === 'rotate';
    const kind = typeOf(rotated ? 'cowork' : type);
    const selfSetup = !rotated && !kind.noMessage;
    const message = selfSetup ? setupMessage(issued.creds) : '';
    const secretOnly = !rotated && bearerOnly(type);
    return (
      <ConnDialog
        title={`${rotated ? 'New secret for' : 'Credentials for'} ${name}`}
        sub={
          rotated
            ? 'The old secret no longer works.'
            : `Add them in ${appOf(type)}. This window updates when it connects.`
        }
        onClose={onClose}
        cta={{ label: connected ? 'Done' : 'Done, I’ll finish later', onClick: onClose }}
      >
        <div className="cx-warn">
          <span>●</span>
          <span>
            Copy the {secretOnly ? 'secret' : 'client secret'} now: it’s shown only once. Anyone who
            has it can act as {name}.
          </span>
        </div>
        {selfSetup ? (
          <div className="cx-self">
            <div className="cx-steps-head">EASIEST: PASTE THIS INTO {kind.app.toUpperCase()}</div>
            <p className="cx-self-body">
              {kind.app} adds the connector itself from this message. Paste it into a new chat and
              send.
            </p>
            <div className="cx-self-msg">{message}</div>
            <div className="cx-self-foot">
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                onClick={(e) => void copy('msg', message, e)}
              >
                {state('msg') === 'copied'
                  ? 'Copied'
                  : state('msg') === 'failed'
                    ? 'Couldn’t copy'
                    : 'Copy message'}
              </button>
              <span className="cx-note">
                The message holds your secret, so it stays in your {kind.app} chat history. Delete
                the chat once it’s connected, or rotate the secret if others can see your chats.
              </span>
            </div>
          </div>
        ) : !rotated ? (
          <p className="cx-note">{kind.noMessage}</p>
        ) : null}
        <CxCopyRow
          label="MCP address"
          value={issued.creds.mcpUrl}
          copied={state('url')}
          onCopy={(e) => void copy('url', issued.creds.mcpUrl, e)}
        />
        {secretOnly ? (
          <CxCopyRow
            label="Secret"
            value={issued.creds.clientId}
            copied={state('id')}
            onCopy={(e) => void copy('id', issued.creds.clientId, e)}
          />
        ) : (
          <>
            <CxCopyRow
              label="Client ID"
              value={issued.creds.clientId}
              copied={state('id')}
              onCopy={(e) => void copy('id', issued.creds.clientId, e)}
            />
            <CxCopyRow
              label="Client secret"
              value={issued.creds.clientSecret}
              copied={state('secret')}
              onCopy={(e) => void copy('secret', issued.creds.clientSecret, e)}
            />
          </>
        )}
        <CxSteps
          head={`${selfSetup ? 'OR ADD IT BY HAND IN' : 'IN'} ${kind.app.toUpperCase()}`}
          steps={steps}
        />
        <div className="cx-status" data-on={connected ? 'true' : undefined}>
          <span className="cx-status-dot" />
          <span>
            {connected
              ? `${name} is connected. ${rotated ? 'It’s using the new secret.' : 'What it does shows up in Activity.'}`
              : `Waiting for ${name} to connect…`}
          </span>
        </div>
      </ConnDialog>
    );
  }

  if (mode === 'rotate') {
    return (
      <ConnDialog
        title="Rotate the secret?"
        sub={name}
        onClose={onClose}
        cta={{
          label: rotating ? 'Rotating…' : 'Rotate secret',
          danger: true,
          disabled: rotating,
          onClick: async () => {
            setError('');
            try {
              const { data: res } = await rotate({
                variables: { accountId, label: currentLabel || 'Claude Cowork MCP' },
              });
              if (res?.rotateCoworkMcpCredentials) {
                setIssued({ creds: res.rotateCoworkMcpCredentials, at: Date.now() });
                onChanged();
              }
            } catch (err) {
              setError(err instanceof Error ? err.message : 'That didn’t work. Try again.');
            }
          },
        }}
      >
        <p className="cx-text">
          The current secret stops working at once, so {name} disconnects until you paste the new
          one into its connector settings. Do this if the secret may have been shared or seen by
          someone else.
        </p>
        {error ? (
          <p className="cx-note" data-bad="true">
            {error}
          </p>
        ) : null}
      </ConnDialog>
    );
  }

  const labelBad = tried && !label.trim();
  return (
    <ConnDialog
      title="Connect an agent"
      sub="It gets its own address and secret. Nothing it makes goes live until you approve it."
      onClose={onClose}
      cta={{
        label: creating ? 'Creating…' : 'Create & issue credentials',
        disabled: creating,
        onClick: async () => {
          if (!label.trim()) return setTried(true);
          setError('');
          try {
            const { data: res } = await create({
              variables: {
                accountId,
                label: label.trim(),
                role: role === 'drafts_only' ? role : null,
              },
            });
            if (res?.createCoworkMcpConnector) {
              setIssued({ creds: res.createCoworkMcpConnector, at: Date.now() });
              onChanged();
            }
          } catch (err) {
            setError(err instanceof Error ? err.message : 'That didn’t work. Try again.');
          }
        },
      }}
    >
      <RadioCards
        label="What are you connecting?"
        options={TYPES}
        value={type}
        onChange={setType}
        columns={2}
      />
      <CxInput
        label="Label"
        value={label}
        onChange={setLabel}
        placeholder={`e.g. Newsroom ${TYPES.find((x) => x.value === type)?.title || 'agent'}`}
        autoFocus
        bad={labelBad}
        note={
          labelBad
            ? 'Give it a name you’ll recognise in Activity.'
            : 'Shown in Activity next to everything it does.'
        }
      />
      <RadioCards label="What it can do" options={ROLES} value={role} onChange={setRole} />
      <div className="cx-info">
        {role === 'drafts_only' ? (
          <>
            <div className="cx-info-title">It only writes drafts</div>
            It can’t publish, change the site or delete. Loosen any of these later on its card here.
          </>
        ) : (
          <>
            <div className="cx-info-title">It starts on Ask me first</div>
            It can write drafts. Publishing, changes to the site and deleting wait for your
            approval. Loosen any of these later on its card here.
          </>
        )}
      </div>
      {error ? (
        <p className="cx-note" data-bad="true">
          {error}
        </p>
      ) : null}
    </ConnDialog>
  );
}
